/** An option list entry — only the parts matching needs. */
interface NamedOption {
  value: string;
  label: string;
}

export type NameMatch =
  /** Exactly one option fits the name — safe to select. */
  | { kind: 'matched'; value: string; label: string }
  /** Several fit. Reported, never picked: choosing for the user would be a guess. */
  | { kind: 'ambiguous'; labels: string[] }
  /** Nothing fits, or the note named nothing. */
  | { kind: 'none' };

/**
 * Case, accents and padding are noise, not meaning: "REVISIÓN" and "revision "
 * name the same group.
 */
function normalize(value: string): string {
  return value
    .normalize('NFD')
    .replace(/\p{Diacritic}/gu, '')
    .toLowerCase()
    .replace(/\s+/g, ' ')
    .trim();
}

/**
 * Resolves a name a note mentioned against the options that actually exist.
 *
 * People write the short form of a name — "Punto de venta de Angel" for the project
 * "Punto de venta de Angel Joan" — so exact equality alone rejects names that are
 * obviously the right one. Matching widens in tiers, and a tier that fits more than
 * one option reports the ambiguity instead of picking: a wrong project silently
 * selected is worse than an empty field that explains itself.
 *
 * Deliberately not fuzzy: no edit distance, no typo tolerance. A misspelling in the
 * note stays unmatched and is surfaced to the user, because "close enough" on a
 * project or an assignee is the kind of guess that quietly files work in the wrong place.
 */
export function matchOptionByName(
  options: NamedOption[],
  name: string | null | undefined,
): NameMatch {
  if (!name?.trim() || options.length === 0) return { kind: 'none' };

  const query = normalize(name);
  if (!query) return { kind: 'none' };

  const candidates = options
    .filter((option) => option.label.trim().length > 0)
    .map((option) => ({ option, normalized: normalize(option.label) }));

  const tiers = [
    // 1. The same name.
    candidates.filter((c) => c.normalized === query),
    // 2. One name is how the other begins — the abbreviated form people actually write.
    candidates.filter((c) => c.normalized.startsWith(query) || query.startsWith(c.normalized)),
    // 3. One name appears inside the other.
    candidates.filter((c) => c.normalized.includes(query) || query.includes(c.normalized)),
  ];

  for (const tier of tiers) {
    if (tier.length === 1) {
      return { kind: 'matched', value: tier[0].option.value, label: tier[0].option.label };
    }
    if (tier.length > 1) {
      return { kind: 'ambiguous', labels: tier.map((c) => c.option.label) };
    }
  }

  return { kind: 'none' };
}
